import apiClient from "./api";
import { API_ENDPOINTS } from "../constants/api";

/**
 * Customer API Service
 * Handles all customer-related API calls
 */
export const customerApi = {
  /**
   * Get all customers (admin only)
   * @returns {Promise} List of all customers
   */
  getAllCustomers: () => {
    return apiClient.post(API_ENDPOINTS.CUSTOMERS.LIST);
  },

  /**
   * Get customer by ID
   * @param {string} customerId - Customer UUID
   * @returns {Promise} Customer details
   */
  getCustomerById: (customerId) => {
    return apiClient.post(API_ENDPOINTS.CUSTOMERS.GET, { customerId });
  },

  /**
   * Create new customer
   * @param {Object} customerData - Customer data
   * @param {string} customerData.name - Customer name (required)
   * @param {string} customerData.description - Customer description (optional)
   * @param {string} customerData.contactEmail - Contact email (optional)
   * @param {string} customerData.contactPerson - Contact person (optional)
   * @param {string} customerData.owner_user_id - Owner user ID (optional, auto-set for regular users)
   * @returns {Promise} Created customer
   */
  createCustomer: (customerData) => {
    return apiClient.post(API_ENDPOINTS.CUSTOMERS.CREATE, customerData);
  },

  /**
   * Update customer
   * @param {string} customerId - Customer UUID
   * @param {Object} customerData - Updated customer data
   * @returns {Promise} Updated customer
   */
  updateCustomer: (customerId, customerData) => {
    return apiClient.post(API_ENDPOINTS.CUSTOMERS.UPDATE, {
      customerId,
      ...customerData,
    });
  },

  /**
   * Delete customer (admin only)
   * @param {string} customerId - Customer UUID
   * @returns {Promise} Success message
   */
  deleteCustomer: (customerId) => {
    return apiClient.post(API_ENDPOINTS.CUSTOMERS.DELETE, { customerId });
  },

  /**
   * Upload customer profile image
   * @param {string} customerId - Customer UUID
   * @param {File} imageFile - Image file to upload
   * @returns {Promise} Updated customer with image URL
   */
  uploadCustomerImage: (customerId, imageFile) => {
    const formData = new FormData();
    formData.append("customerId", customerId);
    formData.append("image", imageFile);

    return apiClient.post(API_ENDPOINTS.CUSTOMERS.UPLOAD_IMAGE, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  /**
   * Delete customer profile image
   * @param {string} customerId - Customer UUID
   * @returns {Promise} Updated customer without image
   */
  deleteCustomerImage: (customerId) => {
    return apiClient.post(API_ENDPOINTS.CUSTOMERS.DELETE_IMAGE, { customerId });
  },
};
